/**
 * MeetingsDashboard.js
 *
 * Autodesk Build-style Meetings module with three tabs:
 * - Meetings: expandable series rows with nested individual meetings
 * - Items: all action/agenda items across meetings
 * - Templates: reusable meeting templates
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

const API = '/technical-meetings';

const STATUS_COLORS = {
  Agenda: '#7c3aed',
  Minutes: '#2563eb',
  Closed: '#16a34a',
  Draft: '#9ca3af',
};

const ITEM_STATUSES = ['Open', 'In Progress', 'Closed'];
const ITEM_TYPES = ['agenda', 'action', 'note', 'information'];
const PRIORITIES = ['Low', 'Normal', 'High'];

const MeetingsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalMeetings', projectId || 'global', true);
  const [activeTab, setActiveTab] = useState('meetings');

  // Meetings tab
  const [series, setSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(true);
  const [expandedSeries, setExpandedSeries] = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', meeting_date: '', start_time: '', location: '' });
  const [creating, setCreating] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  // Meeting detail / edit
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [meetingEditForm, setMeetingEditForm] = useState({});

  // Items tab
  const [allItems, setAllItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsSearch, setItemsSearch] = useState('');

  // Meeting items (within expanded meeting)
  const [meetingDetail, setMeetingDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({ item_type: 'agenda', title: '', description: '', assigned_to: '', due_date: '', priority: 'Normal' });
  const [editingItemId, setEditingItemId] = useState(null);

  // Templates tab
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', default_items: [] });
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Filter
  const [seriesSearch, setSeriesSearch] = useState('');

  // AI Transcription
  const [aiStatus, setAiStatus] = useState(null);
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [showPasteTranscript, setShowPasteTranscript] = useState(false);
  const [pastedTranscript, setPastedTranscript] = useState('');
  const [submittingTranscript, setSubmittingTranscript] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [showLinkTeams, setShowLinkTeams] = useState(false);
  const [teamsJoinUrl, setTeamsJoinUrl] = useState('');
  const [linkingTeams, setLinkingTeams] = useState(false);
  const [pullingTeams, setPullingTeams] = useState(false);
  const [showAiHelp, setShowAiHelp] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ---- FETCH ----
  const fetchSeries = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingSeries(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/series`);
      if (r.data?.success) setSeries(r.data.data || []);
    } catch (err) {
      console.error('Fetch series error:', err);
      showToast('Failed to load meetings', 'error');
    } finally {
      setLoadingSeries(false);
    }
  }, [instance, accounts, projectId]);

  const fetchAllItems = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingItems(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/items`);
      if (r.data?.success) setAllItems(r.data.data || []);
    } catch (err) {
      console.error('Fetch items error:', err);
    } finally {
      setLoadingItems(false);
    }
  }, [instance, accounts, projectId]);

  const fetchTemplates = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingTemplates(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/templates`);
      if (r.data?.success) setTemplates(r.data.data || []);
    } catch (err) {
      console.error('Fetch templates error:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [instance, accounts, projectId]);

  const fetchMeetingDetail = useCallback(async (meetingId) => {
    try {
      setLoadingDetail(true);
      setTranscriptData(null);
      setShowTranscriptPanel(false);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/meeting/${meetingId}`);
      if (r.data?.success) {
        setMeetingDetail(r.data.data);
        try {
          const tr = await authenticatedApiCall(instance, accounts, 'GET', `${API}/meeting/${meetingId}/transcript`);
          if (tr.data?.success) setTranscriptData(tr.data.data);
        } catch (err) { /* transcript fetch optional */ }
      }
    } catch (err) {
      console.error('Fetch meeting detail error:', err);
    } finally {
      setLoadingDetail(false);
    }
  }, [instance, accounts]);

  const fetchAiStatus = useCallback(async () => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/ai-status`);
      if (r.data?.success) setAiStatus(r.data.data);
    } catch (err) { console.error('AI status check failed:', err); }
  }, [instance, accounts]);

  const fetchTranscript = useCallback(async (meetingId) => {
    try {
      setLoadingTranscript(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/meeting/${meetingId}/transcript`);
      if (r.data?.success) setTranscriptData(r.data.data);
    } catch (err) { console.error('Fetch transcript error:', err); }
    finally { setLoadingTranscript(false); }
  }, [instance, accounts]);

  const handleUploadAudio = useCallback(async (meetingId, file) => {
    setUploadingAudio(true);
    try {
      const formData = new FormData();
      formData.append('audio', file);
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/meeting/${meetingId}/upload-audio`, formData);
      if (r.data?.success) {
        showToast('Audio uploaded - transcription in progress...');
        setTimeout(() => fetchTranscript(meetingId), 3000);
      } else {
        showToast(r.data?.message || 'Upload failed', 'error');
      }
    } catch (err) { showToast('Failed to upload audio', 'error'); }
    finally { setUploadingAudio(false); }
  }, [instance, accounts, fetchTranscript]);

  const handleSubmitTranscript = useCallback(async (meetingId) => {
    if (!pastedTranscript.trim() || pastedTranscript.trim().length < 50) {
      showToast('Transcript must be at least 50 characters', 'error');
      return;
    }
    setSubmittingTranscript(true);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/meeting/${meetingId}/submit-transcript`, { transcript: pastedTranscript });
      if (r.data?.success) {
        showToast('Transcript submitted - AI processing...');
        setShowPasteTranscript(false);
        setPastedTranscript('');
        setTimeout(() => fetchTranscript(meetingId), 3000);
      }
    } catch (err) { showToast('Failed to submit transcript', 'error'); }
    finally { setSubmittingTranscript(false); }
  }, [instance, accounts, pastedTranscript, fetchTranscript]);

  const handleReprocess = useCallback(async (meetingId) => {
    setReprocessing(true);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/meeting/${meetingId}/reprocess`);
      if (r.data?.success) {
        showToast('Reprocessing transcript...');
        setTimeout(() => fetchTranscript(meetingId), 3000);
      }
    } catch (err) { showToast('Failed to reprocess', 'error'); }
    finally { setReprocessing(false); }
  }, [instance, accounts, fetchTranscript]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        setAudioChunks(chunks);
        setRecording(false);
      };
      recorder.start(1000);
      setMediaRecorder(recorder);
      setRecording(true);
      setAudioChunks([]);
    } catch (err) {
      showToast('Microphone access denied', 'error');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  }, [mediaRecorder]);

  const handleLinkTeams = useCallback(async (meetingId) => {
    if (!teamsJoinUrl.trim()) { showToast('Enter a Teams meeting join URL', 'error'); return; }
    setLinkingTeams(true);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/meeting/${meetingId}/link-teams`, { joinUrl: teamsJoinUrl.trim() });
      if (r.data?.success) {
        showToast('Teams meeting linked');
        setShowLinkTeams(false);
        setTeamsJoinUrl('');
        fetchTranscript(meetingId);
      } else {
        showToast(r.data?.message || 'Failed to link', 'error');
      }
    } catch (err) { showToast('Failed to link Teams meeting', 'error'); }
    finally { setLinkingTeams(false); }
  }, [instance, accounts, teamsJoinUrl, fetchTranscript]);

  const handlePullTeamsTranscript = useCallback(async (meetingId) => {
    setPullingTeams(true);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/meeting/${meetingId}/pull-teams-transcript`);
      if (r.data?.success) {
        showToast('Teams transcript pulled - processing...');
        setTimeout(() => fetchTranscript(meetingId), 3000);
      } else {
        showToast(r.data?.message || 'No transcript available yet', 'error');
      }
    } catch (err) { showToast('Failed to pull Teams transcript', 'error'); }
    finally { setPullingTeams(false); }
  }, [instance, accounts, fetchTranscript]);

  useEffect(() => { fetchSeries(); }, [fetchSeries]);
  useEffect(() => { if (activeTab === 'items') fetchAllItems(); }, [activeTab, fetchAllItems]);
  useEffect(() => { if (activeTab === 'templates') fetchTemplates(); }, [activeTab, fetchTemplates]);
  useEffect(() => { fetchAiStatus(); }, [fetchAiStatus]);

  // ---- SERIES CRUD ----
  const handleCreateSeries = async () => {
    if (!createForm.title.trim()) { showToast('Title is required', 'error'); return; }
    setCreating(true);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/series`, createForm);
      setShowCreateModal(false);
      setCreateForm({ title: '', description: '', meeting_date: '', start_time: '', location: '' });
      fetchSeries();
      showToast('Meeting created');
    } catch (err) {
      showToast('Failed to create meeting', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSeries = async (seriesId) => {
    if (!window.confirm('Delete this meeting series and all its meetings?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/series/${seriesId}`);
      fetchSeries();
      showToast('Series deleted');
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  // ---- FOLLOW-UP ----
  const handleFollowUp = async (seriesId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API}/series/${seriesId}/follow-up`, {
        meeting_date: new Date().toISOString().slice(0, 10),
        status: 'Agenda'
      });
      fetchSeries();
      showToast('Follow-up meeting created');
      setExpandedSeries(prev => new Set([...prev, seriesId]));
    } catch (err) {
      showToast('Failed to create follow-up', 'error');
    }
  };

  // ---- MEETING EDIT ----
  const startEditMeeting = (meeting) => {
    setEditingMeetingId(meeting.id);
    setMeetingEditForm({
      status: meeting.status || 'Agenda',
      meeting_date: meeting.meeting_date ? new Date(meeting.meeting_date).toISOString().slice(0, 10) : '',
      start_time: meeting.start_time || '',
      end_time: meeting.end_time || '',
      location: meeting.location || '',
      description: meeting.description || '',
      attendees: meeting.attendees || '',
    });
  };

  const handleSaveMeeting = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `${API}/meeting/${editingMeetingId}`, meetingEditForm);
      setEditingMeetingId(null);
      fetchSeries();
      showToast('Meeting updated');
    } catch (err) {
      showToast('Failed to update meeting', 'error');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/meeting/${meetingId}`);
      fetchSeries();
      if (meetingDetail?.id === meetingId) setMeetingDetail(null);
      showToast('Meeting deleted');
    } catch (err) {
      showToast('Failed to delete meeting', 'error');
    }
  };

  // ---- ITEMS CRUD ----
  const handleCreateItem = async () => {
    if (!itemForm.title?.trim()) { showToast('Title is required', 'error'); return; }
    try {
      const meetingId = meetingDetail?.id;
      if (!meetingId) return;
      if (editingItemId) {
        await authenticatedApiCall(instance, accounts, 'PUT', `${API}/items/${editingItemId}`, itemForm);
        showToast('Item updated');
      } else {
        await authenticatedApiCall(instance, accounts, 'POST', `${API}/meeting/${meetingId}/items`, itemForm);
        showToast('Item added');
      }
      setShowItemModal(false);
      setItemForm({ item_type: 'agenda', title: '', description: '', assigned_to: '', due_date: '', priority: 'Normal' });
      setEditingItemId(null);
      fetchMeetingDetail(meetingId);
      if (activeTab === 'items') fetchAllItems();
    } catch (err) {
      showToast('Failed to save item', 'error');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/items/${itemId}`);
      if (meetingDetail) fetchMeetingDetail(meetingDetail.id);
      if (activeTab === 'items') fetchAllItems();
      showToast('Item deleted');
    } catch (err) {
      showToast('Failed to delete item', 'error');
    }
  };

  const startEditItem = (item) => {
    setEditingItemId(item.id);
    setItemForm({
      item_type: item.item_type || 'agenda',
      title: item.title || '',
      description: item.description || '',
      assigned_to: item.assigned_to || '',
      due_date: item.due_date ? new Date(item.due_date).toISOString().slice(0, 10) : '',
      priority: item.priority || 'Normal',
      status: item.status || 'Open',
    });
    setShowItemModal(true);
  };

  // ---- TEMPLATES CRUD ----
  const handleSaveTemplate = async () => {
    if (!templateForm.name?.trim()) { showToast('Name is required', 'error'); return; }
    try {
      if (editingTemplateId) {
        await authenticatedApiCall(instance, accounts, 'PUT', `${API}/templates/${editingTemplateId}`, templateForm);
        showToast('Template updated');
      } else {
        await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/templates`, templateForm);
        showToast('Template created');
      }
      setShowTemplateModal(false);
      setTemplateForm({ name: '', description: '', default_items: [] });
      setEditingTemplateId(null);
      fetchTemplates();
    } catch (err) {
      showToast('Failed to save template', 'error');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/templates/${id}`);
      fetchTemplates();
      showToast('Template deleted');
    } catch (err) {
      showToast('Failed to delete template', 'error');
    }
  };

  const startEditTemplate = (t) => {
    setEditingTemplateId(t.id);
    setTemplateForm({ name: t.name || '', description: t.description || '', default_items: t.default_items || [] });
    setShowTemplateModal(true);
  };

  // ---- HELPERS ----
  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
  };

  const toggleSeries = (id) => {
    setExpandedSeries(prev => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); setMeetingDetail(null); } else n.add(id);
      return n;
    });
  };

  const filteredSeries = useMemo(() => {
    if (!seriesSearch) return series;
    const s = seriesSearch.toLowerCase();
    return series.filter(sr =>
      (sr.title || '').toLowerCase().includes(s) ||
      (sr.description || '').toLowerCase().includes(s) ||
      (sr.meetings || []).some(m => (m.location || '').toLowerCase().includes(s) || (m.description || '').toLowerCase().includes(s))
    );
  }, [series, seriesSearch]);

  const filteredItems = useMemo(() => {
    if (!itemsSearch) return allItems;
    const s = itemsSearch.toLowerCase();
    return allItems.filter(i =>
      (i.title || '').toLowerCase().includes(s) ||
      (i.description || '').toLowerCase().includes(s) ||
      (i.assigned_to || '').toLowerCase().includes(s) ||
      (i.series_title || '').toLowerCase().includes(s)
    );
  }, [allItems, itemsSearch]);

  const getDateRange = (meetings) => {
    if (!meetings?.length) return '—';
    const dates = meetings.filter(m => m.meeting_date).map(m => new Date(m.meeting_date));
    if (!dates.length) return '—';
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return min.getTime() === max.getTime() ? fmt(min) : `${fmt(min)} - ${fmt(max)}`;
  };

  // ==================== RENDER ====================

  // Meeting detail panel (shown when clicking a meeting row)
  const renderMeetingDetail = () => {
    if (!meetingDetail && !loadingDetail) return null;
    if (loadingDetail) return (
      <div style={modalOverlayStyle}>
        <div style={{ ...modalContentStyle, maxWidth: '400px', textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: '#6b7280' }}>Loading meeting details...</p>
        </div>
      </div>
    );
    const items = meetingDetail?.items || [];
    return (
      <div style={modalOverlayStyle} onClick={() => { setMeetingDetail(null); setShowItemModal(false); }}>
        <div style={{ ...modalContentStyle, maxWidth: '750px', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Meeting #{meetingDetail.meeting_number}</h3>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[meetingDetail.status] || '#9ca3af', display: 'inline-block' }} />
                  {meetingDetail.status}
                </span>
                {meetingDetail.meeting_date && <span>{formatDate(meetingDetail.meeting_date)}</span>}
                {meetingDetail.start_time && <span>{meetingDetail.start_time}{meetingDetail.end_time ? ` - ${meetingDetail.end_time}` : ''}</span>}
                {meetingDetail.location && <span>{meetingDetail.location}</span>}
              </div>
              {meetingDetail.attendees && <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '4px' }}>Attendees: {meetingDetail.attendees}</div>}
            </div>
            <button onClick={() => { setMeetingDetail(null); setShowItemModal(false); }} style={closeBtnStyle}>&times;</button>
          </div>

          {meetingDetail.description && <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '1rem' }}>{meetingDetail.description}</p>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#374151' }}>Items ({items.length})</h4>
            <button onClick={() => { setEditingItemId(null); setItemForm({ item_type: 'agenda', title: '', description: '', assigned_to: '', due_date: '', priority: 'Normal' }); setShowItemModal(true); }}
              style={{ ...btnPrimary, padding: '6px 14px', fontSize: '0.8rem' }}>+ Add Item</button>
          </div>

          {items.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' }}>No items yet. Add agenda items, actions, or notes.</p>
          ) : (
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Title</th>
                    <th style={thStyle}>Assigned To</th>
                    <th style={thStyle}>Due Date</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Priority</th>
                    <th style={{ ...thStyle, width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={tdStyle}>{item.item_number}</td>
                      <td style={tdStyle}><span style={{ ...typeBadge, backgroundColor: typeColors[item.item_type] || '#e5e7eb' }}>{item.item_type}</span></td>
                      <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title || '—'}</td>
                      <td style={tdStyle}>{item.assigned_to || '—'}</td>
                      <td style={tdStyle}>{formatDate(item.due_date)}</td>
                      <td style={tdStyle}><span style={{ color: item.status === 'Closed' ? '#16a34a' : item.status === 'In Progress' ? '#d97706' : '#6b7280', fontWeight: 500, fontSize: '0.8rem' }}>{item.status}</span></td>
                      <td style={tdStyle}><span style={{ color: item.priority === 'High' ? '#dc2626' : item.priority === 'Low' ? '#9ca3af' : '#374151', fontSize: '0.8rem' }}>{item.priority}</span></td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => startEditItem(item)} style={miniBtn}>Edit</button>
                          <button onClick={() => handleDeleteItem(item.id)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* AI Transcript Section */}
          {aiStatus?.openaiConfigured && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  AI Meeting Notes
                  {transcriptData?.transcript_status === 'complete' && <span style={{ color: '#16a34a', fontSize: '0.75rem' }}>(Ready)</span>}
                  {transcriptData?.transcript_status === 'processing' && <span style={{ color: '#d97706', fontSize: '0.75rem' }}>(Processing...)</span>}
                  {transcriptData?.transcript_status === 'queued' && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>(Queued)</span>}
                  {transcriptData?.transcript_status === 'failed' && <span style={{ color: '#dc2626', fontSize: '0.75rem' }}>(Failed)</span>}
                  <button onClick={() => setShowAiHelp(true)} title="How AI Meeting Notes works"
                    style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: '0.7rem', color: '#6b7280', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>?</button>
                </h4>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {!transcriptData?.transcript_text && (
                    <>
                      <label title={!aiStatus?.speechConfigured ? 'Azure Speech-to-Text not configured' : ''}
                        style={{ ...miniBtn, color: '#7c3aed', borderColor: '#c4b5fd', cursor: aiStatus?.speechConfigured ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: aiStatus?.speechConfigured ? 1 : 0.5 }}>
                        {uploadingAudio ? 'Uploading...' : 'Upload Audio'}
                        <input type="file" accept="audio/*,.wav,.mp3,.m4a,.webm,.ogg,.flac" style={{ display: 'none' }}
                          onChange={(e) => { if (e.target.files[0]) handleUploadAudio(meetingDetail.id, e.target.files[0]); }}
                          disabled={uploadingAudio || !aiStatus?.speechConfigured} />
                      </label>
                      {!recording ? (
                        <button onClick={startRecording} disabled={!aiStatus?.speechConfigured}
                          title={!aiStatus?.speechConfigured ? 'Azure Speech-to-Text not configured' : ''}
                          style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca', opacity: aiStatus?.speechConfigured ? 1 : 0.5, cursor: aiStatus?.speechConfigured ? 'pointer' : 'not-allowed' }}>
                          Record
                        </button>
                      ) : (
                        <button onClick={stopRecording} style={{ ...miniBtn, color: '#fff', backgroundColor: '#dc2626', borderColor: '#dc2626' }}>
                          Stop Recording
                        </button>
                      )}
                      <button onClick={() => setShowPasteTranscript(p => !p)} style={{ ...miniBtn, color: '#2563eb', borderColor: '#bfdbfe' }}>
                        Paste Transcript
                      </button>
                      {aiStatus?.graphConfigured && (
                        <button onClick={() => setShowLinkTeams(p => !p)} style={{ ...miniBtn, color: '#6366f1', borderColor: '#a5b4fc' }}>
                          Link Teams
                        </button>
                      )}
                    </>
                  )}
                  {transcriptData?.transcript_text && (
                    <>
                      <button onClick={() => setShowTranscriptPanel(p => !p)} style={{ ...miniBtn, color: '#6b7280' }}>
                        {showTranscriptPanel ? 'Hide Transcript' : 'View Transcript'}
                      </button>
                      <button onClick={() => handleReprocess(meetingDetail.id)} disabled={reprocessing}
                        style={{ ...miniBtn, color: '#7c3aed', borderColor: '#c4b5fd', opacity: reprocessing ? 0.6 : 1 }}>
                        {reprocessing ? 'Reprocessing...' : 'Reprocess'}
                      </button>
                    </>
                  )}
                  <button onClick={() => fetchTranscript(meetingDetail.id)} style={{ ...miniBtn, color: '#6b7280' }}>Refresh</button>
                </div>
              </div>

              {/* Upload recorded audio */}
              {audioChunks.length > 0 && !recording && (
                <div style={{ padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: '#92400e' }}>Recording ready ({(audioChunks.reduce((s, c) => s + c.size, 0) / 1024).toFixed(0)} KB)</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => {
                      const blob = new Blob(audioChunks, { type: 'audio/webm' });
                      const file = new File([blob], 'recording.webm', { type: 'audio/webm' });
                      handleUploadAudio(meetingDetail.id, file);
                      setAudioChunks([]);
                    }} style={{ ...miniBtn, color: '#16a34a', borderColor: '#86efac' }}>Upload & Transcribe</button>
                    <button onClick={() => setAudioChunks([])} style={miniBtn}>Discard</button>
                  </div>
                </div>
              )}

              {/* Paste transcript */}
              {showPasteTranscript && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <textarea value={pastedTranscript} onChange={e => setPastedTranscript(e.target.value)}
                    placeholder="Paste meeting transcript or notes here (at least 50 characters)..."
                    style={{ ...inputStyle, minHeight: '100px', resize: 'vertical', marginBottom: '8px' }} />
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowPasteTranscript(false); setPastedTranscript(''); }} style={miniBtn}>Cancel</button>
                    <button onClick={() => handleSubmitTranscript(meetingDetail.id)} disabled={submittingTranscript}
                      style={{ ...miniBtn, color: '#7c3aed', borderColor: '#c4b5fd', opacity: submittingTranscript ? 0.6 : 1 }}>
                      {submittingTranscript ? 'Submitting...' : 'Process with AI'}
                    </button>
                  </div>
                </div>
              )}

              {/* Link Teams meeting */}
              {showLinkTeams && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '6px' }}>
                    Paste the Teams meeting join URL to link this meeting:
                  </div>
                  <input value={teamsJoinUrl} onChange={e => setTeamsJoinUrl(e.target.value)}
                    placeholder="https://teams.microsoft.com/l/meetup-join/..."
                    style={{ ...inputStyle, marginBottom: '8px' }} />
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowLinkTeams(false); setTeamsJoinUrl(''); }} style={miniBtn}>Cancel</button>
                    <button onClick={() => handleLinkTeams(meetingDetail.id)} disabled={linkingTeams}
                      style={{ ...miniBtn, color: '#6366f1', borderColor: '#a5b4fc', opacity: linkingTeams ? 0.6 : 1 }}>
                      {linkingTeams ? 'Linking...' : 'Link & Monitor'}
                    </button>
                  </div>
                </div>
              )}

              {/* Pull Teams transcript button for linked meetings */}
              {transcriptData?.teams_meeting_id && !transcriptData?.transcript_text && aiStatus?.graphConfigured && (
                <div style={{ padding: '12px', backgroundColor: '#eef2ff', borderRadius: '8px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: '#4338ca' }}>Teams meeting linked. Pull transcript after meeting ends.</span>
                  <button onClick={() => handlePullTeamsTranscript(meetingDetail.id)} disabled={pullingTeams}
                    style={{ ...miniBtn, color: '#4338ca', borderColor: '#a5b4fc', opacity: pullingTeams ? 0.6 : 1 }}>
                    {pullingTeams ? 'Pulling...' : 'Pull Transcript'}
                  </button>
                </div>
              )}

              {/* AI Results */}
              {loadingTranscript ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280', fontSize: '0.85rem' }}>Loading transcript data...</div>
              ) : transcriptData?.ai_summary ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0369a1', marginBottom: '6px' }}>Summary</div>
                    <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{transcriptData.ai_summary}</div>
                  </div>
                  {transcriptData.ai_decisions && (
                    <div style={{ padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#166534', marginBottom: '6px' }}>Decisions</div>
                      <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{transcriptData.ai_decisions}</div>
                    </div>
                  )}
                  {transcriptData.ai_open_questions && (
                    <div style={{ padding: '12px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#92400e', marginBottom: '6px' }}>Open Questions</div>
                      <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{transcriptData.ai_open_questions}</div>
                    </div>
                  )}
                  {transcriptData.transcript_processed_at && (
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'right' }}>
                      Processed {new Date(transcriptData.transcript_processed_at).toLocaleString()} via {transcriptData.transcript_source || 'unknown'}
                    </div>
                  )}
                </div>
              ) : !transcriptData?.transcript_text ? (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9ca3af', fontSize: '0.85rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  No transcript yet. Upload an audio recording, record directly, or paste a transcript to generate AI meeting notes.
                </div>
              ) : null}

              {/* Raw transcript viewer */}
              {showTranscriptPanel && transcriptData?.transcript_text && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', maxHeight: '200px', overflowY: 'auto' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6b7280', marginBottom: '6px' }}>Raw Transcript</div>
                  <div style={{ fontSize: '0.8rem', color: '#374151', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{transcriptData.transcript_text}</div>
                </div>
              )}
            </div>
          )}

          {/* Add/Edit Item inline modal */}
          {showItemModal && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>{editingItemId ? 'Edit Item' : 'Add Item'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select value={itemForm.item_type} onChange={e => setItemForm(p => ({ ...p, item_type: e.target.value }))} style={inputStyle}>
                    {ITEM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select value={itemForm.priority} onChange={e => setItemForm(p => ({ ...p, priority: e.target.value }))} style={inputStyle}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Title *</label>
                  <input value={itemForm.title} onChange={e => setItemForm(p => ({ ...p, title: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Assigned To</label>
                  <input value={itemForm.assigned_to} onChange={e => setItemForm(p => ({ ...p, assigned_to: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Due Date</label>
                  <input type="date" value={itemForm.due_date} onChange={e => setItemForm(p => ({ ...p, due_date: e.target.value }))} style={inputStyle} />
                </div>
                {editingItemId && (
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select value={itemForm.status || 'Open'} onChange={e => setItemForm(p => ({ ...p, status: e.target.value }))} style={inputStyle}>
                      {ITEM_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Description</label>
                  <textarea value={itemForm.description} onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                <button onClick={() => { setShowItemModal(false); setEditingItemId(null); }} style={toolbarBtnStyle}>Cancel</button>
                <button onClick={handleCreateItem} style={btnPrimary}>{editingItemId ? 'Save' : 'Add Item'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, padding: '12px 20px', borderRadius: '8px', color: 'white', fontWeight: '500', backgroundColor: toast.type === 'error' ? '#dc3545' : '#198754', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}

      {/* Page title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem' }}>
        <h2 style={{ margin: 0, color: '#333' }}>Meetings</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={() => setShowAiHelp(true)}
            style={{ ...toolbarBtnStyle, display: 'flex', alignItems: 'center', gap: '6px', color: '#7c3aed', borderColor: '#c4b5fd' }}>
            <span style={{ fontSize: '0.85rem' }}>?</span> AI Meeting Notes Guide
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: '1rem', flexShrink: 0 }}>
        {[
          { key: 'meetings', label: 'Meetings' },
          { key: 'items', label: 'Items' },
          { key: 'templates', label: 'Templates' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px'
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== MEETINGS TAB ==================== */}
      {activeTab === 'meetings' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0, flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowCreateMenu(p => !p)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                Create meeting <span style={{ fontSize: '0.7rem' }}>▾</span>
              </button>
              {showCreateMenu && (
                <div style={dropdownStyle}>
                  <button style={dropdownItemStyle} onClick={() => { setShowCreateMenu(false); setShowCreateModal(true); }}>
                    New Meeting Series
                  </button>
                  {templates.length > 0 && templates.map(t => (
                    <button key={t.id} style={dropdownItemStyle} onClick={async () => {
                      setShowCreateMenu(false);
                      try {
                        await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/series`, {
                          title: t.name, description: t.description, template_items: t.default_items
                        });
                        fetchSeries();
                        showToast(`Meeting created from template "${t.name}"`);
                      } catch { showToast('Failed to create from template', 'error'); }
                    }}>
                      From: {t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input type="text" placeholder="Search meetings..." value={seriesSearch}
                onChange={e => setSeriesSearch(e.target.value)}
                style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', width: '220px', outline: 'none' }}
              />
              <button onClick={fetchSeries} style={toolbarBtnStyle}>Refresh</button>
            </div>
          </div>

          {/* Series table */}
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 180px 120px', padding: '10px 16px', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
              <span style={headerCell}>Meeting series</span>
              <span style={headerCell}>Count</span>
              <span style={headerCell}>Date range</span>
              <span style={headerCell}></span>
            </div>

            {loadingSeries ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>Loading meetings...</div>
            ) : filteredSeries.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
                {seriesSearch ? 'No matching meetings found.' : 'No meetings yet. Click "Create meeting" to get started.'}
              </div>
            ) : filteredSeries.map(sr => {
              const isExpanded = expandedSeries.has(sr.id);
              const meetings = sr.meetings || [];
              return (
                <div key={sr.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {/* Series row */}
                  <div
                    style={{ display: 'grid', gridTemplateColumns: '1fr 120px 180px 120px', padding: '12px 16px', cursor: 'pointer', backgroundColor: isExpanded ? '#fafbfc' : 'white' }}
                    onClick={() => toggleSeries(sr.id)}
                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#fafbfc'; }}
                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'white'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▶</span>
                      <span style={{ fontWeight: 500, color: '#374151', fontSize: '0.9rem' }}>{sr.title}</span>
                    </div>
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      {meetings.length} Meeting{meetings.length !== 1 ? 's' : ''}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>{getDateRange(meetings)}</span>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => handleFollowUp(sr.id)} style={{ ...miniBtn, color: '#2563eb', borderColor: '#bfdbfe' }}>+ Follow-up</button>
                      <button onClick={() => handleDeleteSeries(sr.id)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Del</button>
                    </div>
                  </div>

                  {/* Expanded meetings */}
                  {isExpanded && meetings.length > 0 && (
                    <div style={{ paddingLeft: '32px', paddingRight: '16px', paddingBottom: '8px', backgroundColor: '#fafbfc' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...thStyle, width: '60px' }}>No.</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>Time</th>
                            <th style={thStyle}>Location</th>
                            <th style={{ ...thStyle, width: '120px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {meetings.map(m => {
                            const isEditingThis = editingMeetingId === m.id;
                            return (
                              <tr key={m.id}
                                style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                                onClick={() => { if (!isEditingThis) fetchMeetingDetail(m.id); }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0f4ff'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                              >
                                <td style={tdStyle}>{String(m.meeting_number).padStart(2, '0')}</td>
                                <td style={tdStyle}>
                                  {isEditingThis ? (
                                    <select value={meetingEditForm.status} onChange={e => setMeetingEditForm(p => ({ ...p, status: e.target.value }))} style={{ ...inputStyle, padding: '4px 6px', width: '100px' }} onClick={e => e.stopPropagation()}>
                                      <option value="Agenda">Agenda</option>
                                      <option value="Minutes">Minutes</option>
                                      <option value="Closed">Closed</option>
                                      <option value="Draft">Draft</option>
                                    </select>
                                  ) : (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ width: 3, height: 16, borderRadius: 2, backgroundColor: STATUS_COLORS[m.status] || '#9ca3af', display: 'inline-block' }} />
                                      <span style={{ fontSize: '0.85rem' }}>{m.status}</span>
                                    </span>
                                  )}
                                </td>
                                <td style={tdStyle}>
                                  {isEditingThis ? (
                                    <input type="date" value={meetingEditForm.meeting_date} onChange={e => setMeetingEditForm(p => ({ ...p, meeting_date: e.target.value }))} style={{ ...inputStyle, padding: '4px 6px', width: '130px' }} onClick={e => e.stopPropagation()} />
                                  ) : formatDate(m.meeting_date)}
                                </td>
                                <td style={tdStyle}>
                                  {isEditingThis ? (
                                    <input type="time" value={meetingEditForm.start_time} onChange={e => setMeetingEditForm(p => ({ ...p, start_time: e.target.value }))} style={{ ...inputStyle, padding: '4px 6px', width: '100px' }} onClick={e => e.stopPropagation()} />
                                  ) : (m.start_time || '—')}
                                </td>
                                <td style={tdStyle}>
                                  {isEditingThis ? (
                                    <input value={meetingEditForm.location} onChange={e => setMeetingEditForm(p => ({ ...p, location: e.target.value }))} style={{ ...inputStyle, padding: '4px 6px', width: '140px' }} onClick={e => e.stopPropagation()} />
                                  ) : (m.location || '—')}
                                </td>
                                <td style={tdStyle} onClick={e => e.stopPropagation()}>
                                  {isEditingThis ? (
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <button onClick={handleSaveMeeting} style={{ ...miniBtn, backgroundColor: '#198754', color: 'white', borderColor: '#198754' }}>Save</button>
                                      <button onClick={() => setEditingMeetingId(null)} style={miniBtn}>Cancel</button>
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <button onClick={() => startEditMeeting(m)} style={miniBtn}>Edit</button>
                                      <button onClick={() => handleDeleteMeeting(m.id)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Del</button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination info */}
            {!loadingSeries && filteredSeries.length > 0 && (
              <div style={{ padding: '12px 16px', fontSize: '0.8rem', color: '#9ca3af', borderTop: '1px solid #e5e7eb' }}>
                Showing {filteredSeries.length} of {series.length} series
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== ITEMS TAB ==================== */}
      {activeTab === 'items' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
            <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} across all meetings</span>
            <input type="text" placeholder="Search items..." value={itemsSearch}
              onChange={e => setItemsSearch(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', width: '250px', outline: 'none' }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th style={thStyle}>Series</th>
                  <th style={thStyle}>Meeting</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Assigned To</th>
                  <th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Priority</th>
                </tr>
              </thead>
              <tbody>
                {loadingItems ? (
                  <tr><td colSpan={8} style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>Loading items...</td></tr>
                ) : filteredItems.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>No items found.</td></tr>
                ) : filteredItems.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{item.series_title || '—'}</td>
                    <td style={tdStyle}>#{String(item.meeting_number || '').padStart(2, '0')}</td>
                    <td style={tdStyle}><span style={{ ...typeBadge, backgroundColor: typeColors[item.item_type] || '#e5e7eb' }}>{item.item_type}</span></td>
                    <td style={{ ...tdStyle, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title || '—'}</td>
                    <td style={tdStyle}>{item.assigned_to || '—'}</td>
                    <td style={tdStyle}>{formatDate(item.due_date)}</td>
                    <td style={tdStyle}><span style={{ color: item.status === 'Closed' ? '#16a34a' : item.status === 'In Progress' ? '#d97706' : '#6b7280', fontWeight: 500, fontSize: '0.8rem' }}>{item.status}</span></td>
                    <td style={tdStyle}><span style={{ color: item.priority === 'High' ? '#dc2626' : item.priority === 'Low' ? '#9ca3af' : '#374151', fontSize: '0.8rem' }}>{item.priority}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== TEMPLATES TAB ==================== */}
      {activeTab === 'templates' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
            <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
            <button onClick={() => { setEditingTemplateId(null); setTemplateForm({ name: '', description: '', default_items: [] }); setShowTemplateModal(true); }} style={btnPrimary}>
              + Create Template
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            {loadingTemplates ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>Loading templates...</div>
            ) : templates.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>No templates yet. Create one to speed up meeting creation.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Default Items</th>
                    <th style={thStyle}>Created By</th>
                    <th style={{ ...thStyle, width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(t => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{t.name}</td>
                      <td style={{ ...tdStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description || '—'}</td>
                      <td style={tdStyle}>{Array.isArray(t.default_items) ? t.default_items.length : 0} item(s)</td>
                      <td style={tdStyle}>{t.created_by || '—'}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => startEditTemplate(t)} style={miniBtn}>Edit</button>
                          <button onClick={() => handleDeleteTemplate(t.id)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Create Meeting Series Modal */}
      {showCreateModal && (
        <div style={modalOverlayStyle} onClick={() => setShowCreateModal(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Create Meeting</h3>
              <button onClick={() => setShowCreateModal(false)} style={closeBtnStyle}>&times;</button>
            </div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Meeting Series Title *</label>
                <input value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))} style={inputStyle} placeholder="e.g., Weekly Safety Meeting" />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>First Meeting Date</label>
                  <input type="date" value={createForm.meeting_date} onChange={e => setCreateForm(p => ({ ...p, meeting_date: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Time</label>
                  <input type="time" value={createForm.start_time} onChange={e => setCreateForm(p => ({ ...p, start_time: e.target.value }))} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <input value={createForm.location} onChange={e => setCreateForm(p => ({ ...p, location: e.target.value }))} style={inputStyle} placeholder="e.g., Site Office, Room 201" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setShowCreateModal(false)} style={toolbarBtnStyle}>Cancel</button>
              <button onClick={handleCreateSeries} disabled={creating} style={{ ...btnPrimary, opacity: creating ? 0.6 : 1 }}>
                {creating ? 'Creating...' : 'Create Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div style={modalOverlayStyle} onClick={() => { setShowTemplateModal(false); setEditingTemplateId(null); }}>
          <div style={{ ...modalContentStyle, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editingTemplateId ? 'Edit Template' : 'Create Template'}</h3>
              <button onClick={() => { setShowTemplateModal(false); setEditingTemplateId(null); }} style={closeBtnStyle}>&times;</button>
            </div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Template Name *</label>
                <input value={templateForm.name} onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={templateForm.description} onChange={e => setTemplateForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => { setShowTemplateModal(false); setEditingTemplateId(null); }} style={toolbarBtnStyle}>Cancel</button>
              <button onClick={handleSaveTemplate} style={btnPrimary}>{editingTemplateId ? 'Save' : 'Create Template'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Detail modal */}
      {meetingDetail && renderMeetingDetail()}

      {/* AI Help Modal */}
      {showAiHelp && (
        <div style={modalOverlayStyle} onClick={() => setShowAiHelp(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '680px', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>How AI Meeting Notes Works</h3>
              <button onClick={() => setShowAiHelp(false)} style={closeBtnStyle}>&times;</button>
            </div>

            <div style={{ display: 'grid', gap: '16px', fontSize: '0.85rem', color: '#374151' }}>
              {/* Overview */}
              <div style={{ padding: '16px', background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e40af', marginBottom: '8px' }}>Overview</div>
                <div>AI Meeting Notes automatically transcribes audio recordings and generates structured meeting summaries including key decisions, action items, and open questions. Everything stays within your Azure ecosystem — no third-party services touch your data.</div>
              </div>

              {/* Three Methods */}
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '-8px' }}>Three Ways to Capture Notes</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div style={{ padding: '14px', backgroundColor: '#faf5ff', borderRadius: '10px', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>&#127908;</div>
                  <div style={{ fontWeight: 600, color: '#7c3aed', marginBottom: '6px' }}>Record</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: '1.4' }}>Click "Record" to capture audio directly from your browser microphone. Speak clearly, then click "Stop" and "Upload & Transcribe".</div>
                </div>
                <div style={{ padding: '14px', backgroundColor: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>&#128190;</div>
                  <div style={{ fontWeight: 600, color: '#2563eb', marginBottom: '6px' }}>Upload Audio</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: '1.4' }}>Upload a recording from your phone or device. Supports WAV, MP3, M4A, WebM, OGG, and FLAC formats.</div>
                </div>
                <div style={{ padding: '14px', backgroundColor: '#f0fdf4', borderRadius: '10px', border: '1px solid #86efac' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>&#128203;</div>
                  <div style={{ fontWeight: 600, color: '#16a34a', marginBottom: '6px' }}>Paste Transcript</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: '1.4' }}>Already have notes or a transcript? Paste the text directly and the AI will structure it into summary, decisions, and action items.</div>
                </div>
              </div>

              {/* Teams Integration */}
              <div style={{ padding: '14px', backgroundColor: '#eef2ff', borderRadius: '10px', border: '1px solid #c7d2fe' }}>
                <div style={{ fontWeight: 600, color: '#4338ca', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '1.1rem' }}>&#128100;</span> Teams Integration
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: '1.5' }}>
                  <strong>Automatic:</strong> Add <code style={{ backgroundColor: '#e0e7ff', padding: '1px 5px', borderRadius: '3px', fontSize: '0.78rem' }}>portal@LGLDev.onmicrosoft.com</code> to your Teams meeting invite. The portal will auto-detect the meeting and pull the transcript after it ends.<br />
                  <strong>Manual:</strong> Click "Link Teams" and paste the Teams meeting join URL. After the meeting, click "Pull Transcript".
                </div>
                <div style={{ marginTop: '8px', padding: '10px 12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b', fontSize: '0.78rem', color: '#92400e', lineHeight: '1.5' }}>
                  <strong>&#9888;&#65039; Important:</strong> Someone must start transcription at the beginning of every meeting. Without this, Teams will not generate a transcript to pull.
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '0', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', backgroundColor: '#312e81', color: '#fff', borderRadius: '6px 0 0 6px', fontSize: '0.75rem', fontWeight: 600 }}>
                      <span style={{ fontSize: '1rem' }}>&#8943;</span> More
                    </div>
                    <div style={{ width: 0, height: 0, borderTop: '12px solid transparent', borderBottom: '12px solid transparent', borderLeft: '10px solid #312e81' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', backgroundColor: '#4338ca', color: '#fff', fontSize: '0.75rem', fontWeight: 600 }}>
                      <span style={{ fontSize: '0.85rem' }}>&#9679;</span> Record &amp; transcribe
                    </div>
                    <div style={{ width: 0, height: 0, borderTop: '12px solid transparent', borderBottom: '12px solid transparent', borderLeft: '10px solid #4338ca' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', backgroundColor: '#16a34a', color: '#fff', borderRadius: '0 6px 6px 0', fontSize: '0.75rem', fontWeight: 600 }}>
                      <span style={{ fontSize: '0.85rem' }}>&#9998;</span> Start transcription
                    </div>
                  </div>
                </div>
              </div>

              {/* What AI Generates */}
              <div style={{ padding: '14px', backgroundColor: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a' }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>What the AI Generates</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.8rem' }}>
                  <div><span style={{ color: '#0369a1', fontWeight: 600 }}>Summary</span> — 2-3 paragraph overview of the meeting</div>
                  <div><span style={{ color: '#166534', fontWeight: 600 }}>Decisions</span> — Key decisions that were agreed upon</div>
                  <div><span style={{ color: '#7c3aed', fontWeight: 600 }}>Action Items</span> — Tasks with assigned person and due date (auto-added to Items list)</div>
                  <div><span style={{ color: '#92400e', fontWeight: 600 }}>Open Questions</span> — Deferred or unresolved items</div>
                </div>
              </div>

              {/* Troubleshooting */}
              <div style={{ padding: '14px', backgroundColor: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca' }}>
                <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '8px' }}>Troubleshooting</div>
                <div style={{ fontSize: '0.8rem', lineHeight: '1.6' }}>
                  <strong>Recording picks up no audio / wrong microphone:</strong><br />
                  <span style={{ color: '#6b7280' }}>Click the <strong>lock/tune icon</strong> in Chrome's address bar (left of URL) &rarr; <strong>Site settings</strong> &rarr; <strong>Microphone</strong> &rarr; select the correct mic. Set to "Ask" to be prompted next time.</span><br /><br />
                  <strong>Recording shows "No discussion took place":</strong><br />
                  <span style={{ color: '#6b7280' }}>The audio was likely silence or too short. Record at least 10-15 seconds of clear speech. Check your microphone is working in system settings first.</span><br /><br />
                  <strong>Processing takes too long:</strong><br />
                  <span style={{ color: '#6b7280' }}>Transcription typically takes 30-60 seconds. Click "Refresh" to check status. If stuck on "Processing" for over 5 minutes, try uploading again.</span><br /><br />
                  <strong>Teams transcript not available:</strong><br />
                  <span style={{ color: '#6b7280' }}>Ensure transcription was started during the Teams meeting (someone must click "Start transcription" in Teams). The transcript becomes available a few minutes after the meeting ends.</span>
                </div>
              </div>

              {/* Privacy */}
              <div style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.78rem', color: '#64748b' }}>
                <strong>Privacy & Security:</strong> Audio is transcribed by Azure Speech-to-Text and structured by Azure OpenAI — both within your company's Azure subscription. No third-party services are used. Raw transcripts and AI summaries are stored in your portal database only.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- STYLES ----
const toolbarBtnStyle = {
  padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
};

const btnPrimary = {
  padding: '8px 16px', backgroundColor: '#7c3aed', color: 'white', border: '1px solid #7c3aed',
  borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
};

const miniBtn = {
  padding: '3px 8px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb',
  borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', color: '#374151'
};

const closeBtnStyle = { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' };

const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 50,
  backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '200px', overflow: 'hidden'
};

const dropdownItemStyle = {
  display: 'block', width: '100%', padding: '9px 14px', border: 'none', background: 'none',
  textAlign: 'left', fontSize: '0.85rem', color: '#374151', cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6'
};

const modalOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '500px',
  width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

const headerCell = { fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' };
const thStyle = { padding: '8px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'left', borderBottom: '1px solid #e5e7eb' };
const tdStyle = { padding: '8px 10px', fontSize: '0.85rem', color: '#374151' };
const inputStyle = { padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box', outline: 'none' };
const labelStyle = { display: 'block', fontSize: '0.8rem', color: '#555', marginBottom: '0.25rem' };

const typeBadge = { padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 500, color: '#374151', textTransform: 'capitalize' };
const typeColors = { agenda: '#dbeafe', action: '#fef3c7', note: '#e0e7ff', information: '#d1fae5' };

export default MeetingsDashboard;
